import { supportsPushNotifications } from "../../utils/browserCapabilities";
import { useState } from "react";
import { Alert } from "../alert/Alert";
import { twMerge } from "tailwind-merge";
import { Button, Icon } from "@sellar-io/components";
import { useSellarUser } from "../../hooks";
import {
  generateAndSetFirebaseMessagingToken,
  getDeviceId,
  usePushNotificationStore,
} from "./PushNotificationStore";
import { usePushNotificationDeviceCreate } from "../../hooks/api/v2/pushNotification/usePushNotificationDeviceCreate";
import { usePushNotificationDevicesList } from "../../hooks/api/v2/pushNotification/usePushNotificationDevicesList";

export const EnablePushNotificationAlert = ({
  title,
  message,
  className,
  showSettings,
  deviceDisabled,
}: {
  title: string;
  message: string;
  className?: string;
  showSettings?: boolean;
  deviceDisabled?: boolean;
}) => {
  const [active, setActive] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const { user } = useSellarUser();

  const pushNotificationDeviceCreateMutation =
    usePushNotificationDeviceCreate();

  const pushNotificationDevicesQuery = usePushNotificationDevicesList({});
  const deviceId = getDeviceId();
  const existingDevice = pushNotificationDevicesQuery.data?.pages.flatMap(
    (page) => page.filter((device) => device.deviceId === deviceId),
  )[0];

  const notificationGranted = () => {
    if (!supportsPushNotifications()) return false;
    return Notification.permission === "granted";
  };

  const alreadyEnabled =
    !success &&
    !error &&
    notificationGranted() &&
    existingDevice &&
    !deviceDisabled;

  if (alreadyEnabled || !user) return;

  const enableNotifications = async () => {
    const notificationToken = await generateAndSetFirebaseMessagingToken();
    if (notificationToken) {
      const deviceId = getDeviceId();
      pushNotificationDeviceCreateMutation.mutate(
        {
          notificationToken,
          deviceId,
        },
        {
          onSuccess: () => {
            setSuccess(true);
            if (showSettings)
              usePushNotificationStore.getState().setSettingsActive(true);
          },
          onError: (err) => {
            setError(err.message);
            usePushNotificationStore.getState().setGuideActive(true);
          },
        },
      );
    } else {
      usePushNotificationStore.getState().setGuideActive(true);
    }
  };

  if (error)
    return (
      <Alert
        type="error"
        title="An error occurred. Try again."
        onDismiss={() => {
          setError("");
        }}
        message={error}
        className={twMerge(className)}
      />
    );

  if (success)
    return (
      <Alert
        type="success"
        title="Notifications enabled!"
        onDismiss={() => {
          setSuccess(false);
          setActive(false);
        }}
        message=""
        className={twMerge(className)}
      />
    );

  return (
    active && (
      <Alert
        type="sellar"
        title={title}
        message={message}
        onDismiss={() => setActive(false)}
        className={twMerge(className, "border-zinc-300 bg-zinc-50")}
      >
        <Button color="secondary" onClick={enableNotifications}>
          <Icon icon="BELL" />
          Turn on notifications
        </Button>
      </Alert>
    )
  );
};
